import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { profileApi, UpdateUserProfileDto, UpdateEmployeeProfileDto } from '../api/profileApi';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { queryKeys, invalidateEntityQueries } from '@/shared/lib/react-query';

export const useProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.user_id || '';

  // Используем useQueries для параллельной загрузки userProfile и employeeProfile
  // Это устраняет waterfall эффект
  const [userProfileQuery, employeeQuery] = useQueries({
    queries: [
      {
        queryKey: queryKeys.profile.userProfile(userId),
        queryFn: () => profileApi.getUserProfile(userId),
        enabled: !!userId,
      },
      {
        queryKey: queryKeys.profile.employeeProfile(userId),
        queryFn: () => profileApi.getEmployeeProfile(userId),
        enabled: !!userId,
      },
    ],
  });

  const employee = employeeQuery.data?.rows[0];
  const roleId = employee?.role || '';

  // Роль загружаем только когда получили employee
  const employeeRoleQuery = useQuery({
    queryKey: queryKeys.profile.employeeRole(roleId),
    queryFn: () => profileApi.getEmployeeRole(roleId),
    enabled: !!roleId,
  });

  const updateUserProfile = useMutation({
    mutationFn: (data: UpdateUserProfileDto) =>
      profileApi.updateUserProfile(userId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.profile.userProfile(userId)
      });
    },
  });

  const updateEmployeeProfile = useMutation({
    mutationFn: (data: UpdateEmployeeProfileDto) =>
      profileApi.updateEmployeeProfile(employee?.id || '', data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.profile.employeeProfile(userId)
      });
    },
  });

  return {
    isLoading: userProfileQuery.isLoading || employeeQuery.isLoading || employeeRoleQuery.isLoading,
    userProfile: userProfileQuery.data,
    employee,
    role: employeeRoleQuery.data,
    updateUserProfile,
    updateEmployeeProfile,
  };
};
